import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DatePicker, Dialog, Toast } from 'antd-mobile';
import dayjs from 'dayjs';
import {
  CustomerPickerPopup,
  RecordActionRow,
  RecordBottomActions,
  RecordChoiceGrid,
  RecordCustomerField,
  RecordNoteRow,
  RecordPageHeader,
  RecordQuantityField,
  RecordSummary
} from '@/components/RecordForm';
import { Customer } from '@/types';
import { createSale, deleteSale, getSale, SaleFormData, updateSale } from '@/api/sales';
import { CategoryWithLevels, getCategories } from '@/api/categories';
import { getCustomerDetail } from '@/api/customers';
import useBackHandler, { BACK_PRIORITY } from '@/hooks/useBackHandler';

function formatSaleWeight(value: number) {
  if (!Number.isFinite(value)) return '1.0';
  return Number.isInteger(value) ? value.toFixed(1) : String(value);
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 }).format(value);
}

export default function SaleForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get('customerId');
  const source = searchParams.get('from');
  const requestedReturnTo = searchParams.get('returnTo');
  const statisticsFrom = searchParams.get('statisticsFrom') === 'settings' ? 'settings' : 'home';
  const isEdit = Boolean(id);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [selectorVisible, setSelectorVisible] = useState(!customerIdFromUrl && !isEdit);
  const [categories, setCategories] = useState<CategoryWithLevels[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedPriceLevelId, setSelectedPriceLevelId] = useState('');
  const [weight, setWeight] = useState('1.0');
  const [note, setNote] = useState('');
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [saleTime, setSaleTime] = useState(new Date());
  const [saving, setSaving] = useState(false);
  const [formTouched, setFormTouched] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCategories()
      .then((res) => {
        if (cancelled || res.code !== 0) return;
        setCategories(res.data);
        if (!isEdit && res.data.length > 0) {
          const firstCategory = res.data[0];
          setSelectedCategoryId(String(firstCategory.id));
          setSelectedPriceLevelId(
            firstCategory.priceLevels.length > 0 ? String(firstCategory.priceLevels[0].id) : ''
          );
        }
      })
      .catch((error) => {
        console.error(error);
        Toast.show({ content: '获取分类失败', icon: 'fail' });
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getSale(Number(id))
      .then((res) => {
        if (cancelled || res.code !== 0) return;
        const record = res.data;
        setCustomer({
          id: record.customer_id,
          name: record.customer_name,
          village: record.customer_village || '',
          avatar: record.customer_avatar || undefined,
          phone: '',
          surname: '',
          buy_count: 0,
          total_weight: 0,
          created_at: ''
        });
        setSelectedCategoryId(String(record.category_id));
        setSelectedPriceLevelId(String(record.price_level_id));
        setWeight(formatSaleWeight(Number(record.weight)));
        setNote(record.note || '');
        setSaleTime(new Date(record.sale_time));
        setSelectorVisible(false);
        setFormTouched(false);
      })
      .catch(() => Toast.show({ content: '加载销售记录失败', icon: 'fail' }));
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!customerIdFromUrl || isEdit) return;
    let cancelled = false;
    getCustomerDetail(Number(customerIdFromUrl))
      .then((res) => {
        if (!cancelled && res.code === 0) setCustomer(res.data.customer);
      })
      .catch((error) => console.error(error));
    return () => {
      cancelled = true;
    };
  }, [customerIdFromUrl, isEdit]);

  const currentCategory = useMemo(
    () => categories.find((category) => String(category.id) === selectedCategoryId),
    [categories, selectedCategoryId]
  );

  const currentPriceLevel = useMemo(
    () => currentCategory?.priceLevels.find((level) => String(level.id) === selectedPriceLevelId),
    [currentCategory, selectedPriceLevelId]
  );

  const totalAmount = useMemo(() => {
    const numericWeight = Number(weight);
    if (!currentPriceLevel || !Number.isFinite(numericWeight)) return 0;
    return Number((currentPriceLevel.price * numericWeight).toFixed(2));
  }, [currentPriceLevel, weight]);

  const destination = requestedReturnTo?.startsWith('/')
    ? requestedReturnTo
    : source === 'search'
      ? '/search'
      : source === 'statistics'
        ? `/statistics?from=${statisticsFrom}`
        : customerIdFromUrl || source === 'customer'
          ? `/customer/${customer?.id || customerIdFromUrl}`
          : '/';

  const leave = async () => {
    if (formTouched && !saving) {
      const confirmed = await Dialog.confirm({
        content: '当前内容尚未保存，确认离开吗？',
        confirmText: '离开'
      });
      if (!confirmed) return;
    }
    navigate(destination, { replace: true });
  };

  useBackHandler(datePickerVisible, () => setDatePickerVisible(false), BACK_PRIORITY.modal);
  useBackHandler(selectorVisible, () => setSelectorVisible(false), BACK_PRIORITY.popup);
  useBackHandler(true, leave, BACK_PRIORITY.page);

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!formTouched || saving) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [formTouched, saving]);

  const handleCategorySelect = (categoryId: string) => {
    const category = categories.find((item) => String(item.id) === categoryId);
    setSelectedCategoryId(categoryId);
    setSelectedPriceLevelId(
      category && category.priceLevels.length > 0 ? String(category.priceLevels[0].id) : ''
    );
    setFormTouched(true);
  };

  const handleSubmit = async () => {
    if (!customer) {
      Toast.show({ content: '请选择客户', icon: 'fail' });
      return;
    }
    if (!selectedCategoryId || !selectedPriceLevelId) {
      Toast.show({ content: '请选择分类和档位', icon: 'fail' });
      return;
    }
    const numericWeight = Number(weight);
    if (!Number.isFinite(numericWeight) || numericWeight <= 0) {
      Toast.show({ content: '数量必须大于 0', icon: 'fail' });
      return;
    }

    const data: SaleFormData = {
      customer_id: customer.id,
      category_id: Number(selectedCategoryId),
      price_level_id: Number(selectedPriceLevelId),
      weight: numericWeight,
      sale_time: saleTime.toISOString(),
      note
    };

    setSaving(true);
    try {
      if (isEdit && id) {
        await updateSale(Number(id), data);
      } else {
        await createSale(data);
      }
      Toast.show({ content: isEdit ? '修改成功' : '保存成功', icon: 'success' });
      navigate(destination, { replace: true });
    } catch (error) {
      console.error(error);
      Toast.show({ content: '保存失败', icon: 'fail' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const confirmed = await Dialog.confirm({
      content: '删除后无法恢复，确认删除这条销售记录吗？',
      confirmText: '删除'
    });
    if (!confirmed) return;
    try {
      await deleteSale(Number(id));
      Toast.show({ content: '已删除', icon: 'success' });
      navigate(destination, { replace: true });
    } catch {
      Toast.show({ content: '删除失败', icon: 'fail' });
    }
  };

  const unit = currentPriceLevel?.unit || '斤';
  const compactWeight = Number.isFinite(Number(weight)) ? formatNumber(Number(weight)) : weight;

  return (
    <div className="record-form-page record-form-page--sale">
      <RecordPageHeader
        title={isEdit ? '修改销售' : '记一笔销售'}
        onBack={leave}
        onDelete={isEdit ? handleDelete : undefined}
        deleteLabel="删除销售记录"
      />

      <main className="record-form-content">
        <RecordCustomerField
          customer={customer}
          variant="card"
          onClick={() => setSelectorVisible(true)}
        />

        <section className="record-form-section record-form-section--category">
          <h2 className="record-form-label">酒品分类</h2>
          <RecordChoiceGrid
            options={categories.map((category) => ({
              id: String(category.id),
              label: category.name
            }))}
            selectedId={selectedCategoryId}
            onSelect={handleCategorySelect}
            columns={4}
            ariaLabel="酒品分类"
          />
        </section>

        <section className="record-form-section record-form-section--price">
          <h2 className="record-form-label">价格档位</h2>
          <RecordChoiceGrid
            options={(currentCategory?.priceLevels || []).map((level) => ({
              id: String(level.id),
              label: level.name
            }))}
            selectedId={selectedPriceLevelId}
            onSelect={(priceLevelId) => {
              setSelectedPriceLevelId(priceLevelId);
              setFormTouched(true);
            }}
            columns={3}
            variant="radio"
            ariaLabel="价格档位"
          />
        </section>

        <section className="record-form-section record-form-section--quantity">
          <h2 className="record-form-label">数量</h2>
          <RecordQuantityField
            value={weight}
            unit={unit}
            step="0.5"
            variant="sale"
            onChange={(event) => {
              setWeight(event.target.value);
              setFormTouched(true);
            }}
          />
        </section>

        <div className="record-meta-list">
          <RecordActionRow
            label="时间"
            value={dayjs(saleTime).format('M月D日 HH:mm')}
            onClick={() => setDatePickerVisible(true)}
          />
          <RecordNoteRow
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setFormTouched(true);
            }}
          />
        </div>
      </main>

      <RecordSummary
        variant="sale"
        label="总价"
        value={<>¥ {formatNumber(totalAmount)}</>}
        detail={<>{currentPriceLevel?.name || `0元/${unit}`} × {compactWeight}{unit}</>}
      />

      <RecordBottomActions saving={saving} onCancel={leave} onSave={handleSubmit} />

      <CustomerPickerPopup
        visible={selectorVisible}
        selectedId={customer?.id}
        onClose={() => setSelectorVisible(false)}
        onSelect={(selectedCustomer) => {
          setCustomer(selectedCustomer);
          setSelectorVisible(false);
          setFormTouched(true);
        }}
      />

      <DatePicker
        visible={datePickerVisible}
        value={saleTime}
        onClose={() => setDatePickerVisible(false)}
        onConfirm={(value) => {
          setSaleTime(value);
          setFormTouched(true);
        }}
        precision="minute"
        renderLabel={(type, data) => {
          const suffix = { year: '年', month: '月', day: '日', hour: '时', minute: '分' };
          return `${data}${suffix[type as keyof typeof suffix]}`;
        }}
      />
    </div>
  );
}
